import type {
    Regime,
    SpecialistSlug,
    Stage,
    Stance,
    Tone,
    Verdict,
    WavePhase
} from '@/lib/market/types'

type Labelled = {
    label: string
    tone: Tone
}

export const STAGES: Record<Stage, Labelled & { short: string, description: string }> = {
    early: {
        label: 'Khởi sóng sớm',
        short: 'Khởi sóng',
        tone: 'positive',
        description: 'Vừa lấy lại MA50 sau xu hướng giảm, đáy sau cao hơn đáy trước.'
    },
    near: {
        label: 'Sát điểm mua',
        short: 'Sát pivot',
        tone: 'positive',
        description: 'Giá nằm trong vòng 5% dưới đỉnh nền, khối lượng cạn dần.'
    },
    breakout: {
        label: 'Vừa breakout',
        short: 'Breakout',
        tone: 'positive',
        description: 'Giá vừa vượt đỉnh nền, còn trong vùng mua 6% phía trên pivot.'
    },
    basing: {
        label: 'Đang tích nền',
        short: 'Tích nền',
        tone: 'neutral',
        description: 'Đang xây nền giá, chưa tiến sát điểm pivot.'
    },
    extended: {
        label: 'Đã chạy xa',
        short: 'Chạy xa',
        tone: 'neutral',
        description: 'Xu hướng tăng tốt nhưng giá đã rời xa vùng mua an toàn.'
    },
    sideway: {
        label: 'Đi ngang',
        short: 'Đi ngang',
        tone: 'neutral',
        description: 'Dao động quanh các đường trung bình, chưa có xu hướng.'
    },
    distribution: {
        label: 'Suy yếu',
        short: 'Suy yếu',
        tone: 'negative',
        description: 'Mất MA20 với khối lượng bán lớn hơn khối lượng mua.'
    },
    downtrend: {
        label: 'Xu hướng giảm',
        short: 'Giảm',
        tone: 'negative',
        description: 'Nằm dưới MA50 và MA200, MA50 dốc xuống.'
    }
}

export const STAGE_ORDER: Stage[] = ['early', 'near', 'breakout', 'basing', 'extended', 'sideway', 'distribution', 'downtrend']

export const VERDICTS: Record<Verdict, Labelled & { description: string }> = {
    'mua-som': {
        label: 'Mua sớm',
        tone: 'positive',
        description: 'Đủ đồng thuận và điểm vào đang mở.'
    },
    'cho-diem-mua': {
        label: 'Chờ điểm mua',
        tone: 'positive',
        description: 'Nền tảng tốt, cần tín hiệu kích hoạt.'
    },
    'cho-nhip-chinh': {
        label: 'Chờ nhịp chỉnh',
        tone: 'neutral',
        description: 'Xu hướng mạnh nhưng giá đã chạy xa.'
    },
    'quan-sat': {
        label: 'Quan sát',
        tone: 'neutral',
        description: 'Tín hiệu chưa đủ đồng thuận.'
    },
    'dung-ngoai': {
        label: 'Đứng ngoài',
        tone: 'negative',
        description: 'Rủi ro lớn hơn cơ hội.'
    }
}

export const VERDICT_ORDER: Verdict[] = ['mua-som', 'cho-diem-mua', 'cho-nhip-chinh', 'quan-sat', 'dung-ngoai']

export const STANCES: Record<Stance, Labelled> = {
    bullish: { label: 'Tích cực', tone: 'positive' },
    neutral: { label: 'Trung lập', tone: 'neutral' },
    bearish: { label: 'Tiêu cực', tone: 'negative' }
}

export const PHASES: Record<WavePhase, Labelled & { quadrant: string, description: string }> = {
    'khoi-song': {
        label: 'Khởi sóng',
        quadrant: 'Cải thiện',
        tone: 'positive',
        description: 'Sức mạnh còn thấp nhưng động lượng đang tăng nhanh: vùng đón sóng sớm.'
    },
    'song-manh': {
        label: 'Sóng mạnh',
        quadrant: 'Dẫn dắt',
        tone: 'positive',
        description: 'Mạnh hơn thị trường và vẫn tăng tốc: đang dẫn dắt dòng tiền.'
    },
    'cuoi-song': {
        label: 'Cuối sóng',
        quadrant: 'Suy yếu',
        tone: 'neutral',
        description: 'Vẫn mạnh hơn thị trường nhưng động lượng chậm lại: dòng tiền bắt đầu rút.'
    },
    'ngoai-song': {
        label: 'Ngoài sóng',
        quadrant: 'Tụt hậu',
        tone: 'negative',
        description: 'Yếu hơn thị trường và chưa có động lượng: chưa có dòng tiền quay lại.'
    }
}

export const PHASE_ORDER: WavePhase[] = ['khoi-song', 'song-manh', 'cuoi-song', 'ngoai-song']

export const REGIMES: Record<Regime, Labelled & { description: string }> = {
    'risk-on': { label: 'Risk-on', tone: 'positive', description: 'Bối cảnh ủng hộ mở vị thế mới.' },
    neutral: { label: 'Trung tính', tone: 'neutral', description: 'Giải ngân chọn lọc, tỷ trọng vừa phải.' },
    'risk-off': { label: 'Risk-off', tone: 'negative', description: 'Ưu tiên bảo toàn vốn.' }
}

// Short fixed labels for the six specialists, used in generated sentences and
// compact table headers. The full titles and descriptions live in the database.
export const SPECIALIST_LABELS: Record<SpecialistSlug, { short: string, column: string }> = {
    quant: { short: 'Kỹ thuật định lượng', column: 'Kỹ thuật' },
    fundamental: { short: 'Cơ bản và định giá', column: 'Cơ bản' },
    flow: { short: 'Dòng tiền', column: 'Dòng tiền' },
    macro: { short: 'Vĩ mô và ngành', column: 'Vĩ mô' },
    sentiment: { short: 'Tin tức và tâm lý', column: 'Tin tức' },
    risk: { short: 'Quản trị rủi ro', column: 'Rủi ro' }
}

export const SPECIALIST_ORDER: SpecialistSlug[] = ['quant', 'fundamental', 'flow', 'macro', 'sentiment', 'risk']

export const NEWS_CATEGORIES: Record<string, string> = {
    'vi-mo': 'Vĩ mô',
    'doanh-nghiep': 'Doanh nghiệp',
    'dong-tien': 'Dòng tiền',
    'chinh-sach': 'Chính sách',
    'quoc-te': 'Quốc tế',
    nganh: 'Ngành'
}

export const SENTIMENTS: Record<string, Labelled> = {
    'tich-cuc': { label: 'Tích cực', tone: 'positive' },
    'trung-tinh': { label: 'Trung tính', tone: 'neutral' },
    'tieu-cuc': { label: 'Tiêu cực', tone: 'negative' }
}

export const sentimentOf = (value: string): Labelled => SENTIMENTS[value] ?? SENTIMENTS['trung-tinh']

export const categoryOf = (value: string): string => NEWS_CATEGORIES[value] ?? 'Thị trường'

export const IMPACT_LABELS: Record<number, string> = {
    1: 'Tác động thấp',
    2: 'Tác động trung bình',
    3: 'Tác động cao'
}

export const JOURNAL_STATUSES: Record<string, Labelled> = {
    'theo-doi': { label: 'Đang theo dõi', tone: 'neutral' },
    'da-vao-lenh': { label: 'Đã vào lệnh', tone: 'positive' },
    'da-dong': { label: 'Đã đóng', tone: 'negative' }
}

export const INVESTOR_STYLES: Record<string, string> = {
    'luot-song': 'Lướt sóng ngắn hạn',
    'trung-han': 'Đầu tư trung hạn',
    'dai-han': 'Tích sản dài hạn'
}

export const PLANS: Record<string, { label: string, watchlistLimit: number | null }> = {
    free: { label: 'Free', watchlistLimit: 12 },
    pro: { label: 'Pro', watchlistLimit: null },
    team: { label: 'Team', watchlistLimit: null }
}

export const toneText: Record<Tone, string> = {
    positive: 'text-lime',
    negative: 'text-coral',
    neutral: 'text-gold'
}

export const toneBorder: Record<Tone, string> = {
    positive: 'border-lime/40 text-lime',
    negative: 'border-coral/45 text-coral',
    neutral: 'border-gold/40 text-gold'
}

export const toneBg: Record<Tone, string> = {
    positive: 'bg-lime',
    negative: 'bg-coral',
    neutral: 'bg-gold'
}
